/**
 * Groups letters by summary and returns the grouped structure
 * @param {Array} letters - Array of letters to group
 * @returns {Object} Grouped letters by summary key
 */
const groupLettersBySummary = (letters) => {
    const grouped = {};
    
    letters.forEach(letter => {
        let groupKey = "no summary assigned";
        
        if (letter?.summary) {
            
            if (letter.summary?.id) {
                groupKey = letter.summary.id;
            }
        }
        
        if (!grouped[groupKey]) {
            grouped[groupKey] = [];
        }
        grouped[groupKey].push(letter);
    });
    
    return grouped;
};

/**
 * Sorts summary groups by summaryOrder if available
 * @param {Object} grouped - Grouped letters object
 * @param {Object} summaryOrder - Stack summary order configuration
 * @returns {Array} Sorted array of [groupName, groupData] entries
 */
const sortSummaryGroups = (grouped, summaryOrder) => {
    let sortedGroups = Object.entries(grouped);
    
    if (summaryOrder && Object.keys(summaryOrder).length > 0) {
        sortedGroups.sort(([groupNameA, groupDataA], [groupNameB, groupDataB]) => {
            const summaryIdA = groupDataA[0]?.summary?.id;
            const summaryIdB = groupDataB[0]?.summary?.id;
            
            const orderA = summaryOrder[summaryIdA] || Number.MAX_SAFE_INTEGER;
            const orderB = summaryOrder[summaryIdB] || Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });
    }
    
    return sortedGroups;
};

/**
 * Sorts letters within a group based on summary notes and summary order
 * @param {Array} groupData - Letters in a summary group
 * @param {Object} summaryOrder - Stack summary order configuration
 * @returns {Array} Sorted letters within the group
 */
const sortLettersWithinGroup = (groupData, summaryOrder) => {
    let sortedGroupData = [...groupData];
    
    // Sort letters: put letters with summaryNotes at the bottom
    sortedGroupData.sort((a, b) => {
        const aHasNotes = a.summaryNotes && a.summaryNotes.trim();
        const bHasNotes = b.summaryNotes && b.summaryNotes.trim();
        
        if (aHasNotes && !bHasNotes) return 1;  // a goes to bottom
        if (!aHasNotes && bHasNotes) return -1; // b goes to bottom
        if (aHasNotes && bHasNotes) {
            // Both have notes, sort by date (earliest first)
            const dateA = new Date(a.receivedDate || a.createdDate || 0);
            const dateB = new Date(b.receivedDate || b.createdDate || 0);
            return dateA - dateB;
        }
        
        // For letters without notes, sort by date (earliest first)
        const dateA = new Date(a.receivedDate || a.createdDate || 0);
        const dateB = new Date(b.receivedDate || b.createdDate || 0);
        return dateA - dateB;
    });
    
    return sortedGroupData;
};

/**
 * Sorts and groups letters by summary with proper ordering based on stack configuration
 * @param {Object} stack - The stack object containing summaryOrder
 * @param {Array} letters - Array of letters to sort and group
 * @param {boolean} isGrouped - Whether to group by summary
 * @param {string} stackType - The stack type (e.g., "ANSWER")
 * @returns {Array} Sorted and grouped letters ready for table display
 */
export const getSortedGroupedLetters = (stack, letters, isGrouped = false, stackType = null) => {
    if (!isGrouped || (stackType !== "ANSWER" && stackType !== "REVIEW_ANSWER")) {
        return letters;
    }

    // Use helper functions for common logic
    const grouped = groupLettersBySummary(letters);
    const sortedGroups = sortSummaryGroups(grouped, stack.summaryOrder);

    // Convert to array format for Ant Design Table with horizontal colSpan
    const result = [];
    
    sortedGroups.forEach(([groupName, groupData]) => {
        const sortedGroupData = sortLettersWithinGroup(groupData, stack.summaryOrder);
        
        // Add summary header row that will span horizontally
        result.push({
            key: `summary-${groupName}`,
            isSummaryRow: true,
            summaryName: groupName,
            summaryCount: sortedGroupData.length,
            summaryData: sortedGroupData[0]?.summary || null,
            // Kept for shape only: the Name column computes the real span
            // from the table's own column count, which differs between answer
            // and review stacks.
            nameColSpan: 8,
            nameRowSpan: 1
        });
        
        // Add letter rows normally (now sorted)
        sortedGroupData.forEach(letter => {
            result.push({
                ...letter,
                key: letter.id,
                isSummaryRow: false,
                nameColSpan: 1,
                nameRowSpan: 1
            });
        });
    });

    return result;
};

/**
 * Returns a flat array of letters sorted by summaries and summary notes
 * @param {Object} stack - The stack object containing summaryOrder
 * @param {Array} lettersByStack - Array of ProcessedLetterDto letters
 * @returns {Array} Flat array of sorted letters without grouping structure
 */
export const getSortedLettersFlat = (stack, lettersByStack) => {
    if (!lettersByStack || lettersByStack.length === 0) {
        return [];
    }

    // Use helper functions for common logic
    const grouped = groupLettersBySummary(lettersByStack);
    const sortedGroups = sortSummaryGroups(grouped, stack.summaryOrder);

    // Flatten the sorted groups into a single array
    const result = [];
    
    sortedGroups.forEach(([groupName, groupData]) => {
        const sortedGroupData = sortLettersWithinGroup(groupData, stack.summaryOrder);
        
        // Add all sorted letters from this group to the result
        result.push(...sortedGroupData);
    });

    return result;
};
